export const FLOW = [
  { id: "cms", title: "CMS Admin", sub: "Upload PDF / URL / Teks · Kelola konten", tag: "React Admin" },
  { id: "ingest", title: "Ingestion Pipeline", sub: "Extract → Clean → Chunk (900 char, overlap 150)", tag: "FastAPI Worker" },
  { id: "kb", title: "Knowledge Base", sub: "Dokumen + Chunk + Embedding / Text Index", tag: "PGVector · Mongo" },
  { id: "agent", title: "AI Agent Brain", sub: "Intent Detector → Retriever → Guardrail Prompt → LLM", tag: "GPT · RAG" },
  { id: "chat", title: "Chatbot Front-end", sub: "SSE streaming · quick replies · sumber KB", tag: "Widget" },
  { id: "wa", title: "WhatsApp Handoff", sub: "wa.me deep link + ringkasan otomatis · Lead CRM", tag: "WA Admin" },
];

export const DATA_FLOW = [
  "Admin mengunggah dokumen regulasi (PDF/URL/teks) di AI Knowledge Base Manager.",
  "Backend mengekstrak teks (pypdf / BeautifulSoup), membersihkan, lalu memecah menjadi chunk berukuran ±900 karakter dengan overlap 150.",
  "Chunk disimpan ke tabel kb_chunks + embedding vektor (PGVector, produksi) / text index berbobot (MVP saat ini).",
  "Pengunjung bertanya di widget chat → request POST /api/chat/stream (SSE).",
  "Intent Detector (rule-based berbobot) menilai sinyal: harga, penawaran/MoU, urgensi, kirim berkas, minta kontak.",
  "Retriever mengambil Top-K chunk paling relevan dari dokumen yang aktif.",
  "Prompt builder menyusun: System Prompt (persona + guardrail + anti-halusinasi) + riwayat 10 pesan + <konteks_knowledge_base> + <intent_tinggi>.",
  "LLM menghasilkan jawaban yang di-stream token-per-token ke pengguna.",
  "Post-processing: deteksi penolakan off-topic, simpan chat_messages, update chat_sessions (layanan, intent).",
  "Jika skor intent ≥ ambang → buat/ubah Lead (source=chatbot), bangun ringkasan WA terformat, kirim tombol 'Konsultasi Lanjutan via WhatsApp Admin'.",
  "Klik tombol → wa.me/{nomor_admin_aktif}?text={ringkasan} terbuka; klik dicatat untuk analytics konversi.",
];

export const SQL_SCHEMA = `-- ============================================================
-- PT Rikonstruksi Utama — PostgreSQL 16 + pgvector (Target Produksi)
-- ============================================================
CREATE EXTENSION IF NOT EXISTS vector;
CREATE EXTENSION IF NOT EXISTS pgcrypto;

CREATE TYPE lead_status AS ENUM ('baru','dihubungi','dokumen_diterima','proses','selesai','batal');
CREATE TYPE lead_source AS ENUM ('website','chatbot','whatsapp','manual');
CREATE TYPE kb_category AS ENUM ('regulasi','persyaratan','matrix_estimasi','profil','lainnya');
CREATE TYPE kb_source   AS ENUM ('pdf','url','text','file');
CREATE TYPE msg_role    AS ENUM ('user','assistant','system');

-- ---------- AUTH ----------
CREATE TABLE users (
  id            UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  email         CITEXT UNIQUE NOT NULL,
  password_hash TEXT NOT NULL,              -- bcrypt
  name          TEXT,
  role          TEXT NOT NULL DEFAULT 'editor' CHECK (role IN ('superadmin','admin','editor','sales')),
  last_login_at TIMESTAMPTZ,
  created_at    TIMESTAMPTZ NOT NULL DEFAULT now()
);

-- ---------- CMS CONTENT ----------
CREATE TABLE services (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  slug TEXT UNIQUE NOT NULL, title TEXT NOT NULL, icon TEXT,
  short TEXT, description TEXT, features JSONB DEFAULT '[]',
  sort_order INT DEFAULT 0, is_active BOOLEAN DEFAULT true,
  created_at TIMESTAMPTZ DEFAULT now(), updated_at TIMESTAMPTZ DEFAULT now()
);
CREATE TABLE portfolio (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  title TEXT NOT NULL, client TEXT, category TEXT, location TEXT, year SMALLINT,
  result TEXT, image_url TEXT, description TEXT, service_id UUID REFERENCES services(id),
  created_at TIMESTAMPTZ DEFAULT now(), updated_at TIMESTAMPTZ DEFAULT now()
);
CREATE TABLE articles (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  slug TEXT UNIQUE NOT NULL, title TEXT NOT NULL, category TEXT,
  excerpt TEXT, content TEXT, cover_url TEXT, read_time SMALLINT,
  seo_title TEXT, seo_description TEXT,
  published BOOLEAN DEFAULT false, published_at TIMESTAMPTZ,
  author_id UUID REFERENCES users(id),
  created_at TIMESTAMPTZ DEFAULT now(), updated_at TIMESTAMPTZ DEFAULT now()
);
CREATE TABLE faqs (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  question TEXT NOT NULL, answer TEXT NOT NULL, category TEXT, sort_order INT DEFAULT 0
);

-- ---------- SETTINGS (key-value JSONB) ----------
CREATE TABLE settings (
  key        TEXT PRIMARY KEY,             -- 'profile' | 'ai' | 'landing'
  value      JSONB NOT NULL,
  updated_by UUID REFERENCES users(id),
  updated_at TIMESTAMPTZ DEFAULT now()
);
CREATE TABLE wa_admin_numbers (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  name TEXT NOT NULL, phone_e164 TEXT NOT NULL CHECK (phone_e164 ~ '^[1-9][0-9]{7,14}$'),
  is_active BOOLEAN DEFAULT true, weight SMALLINT DEFAULT 1,       -- round-robin berbobot
  handled_count INT DEFAULT 0, created_at TIMESTAMPTZ DEFAULT now()
);

-- ---------- KNOWLEDGE BASE (RAG) ----------
CREATE TABLE kb_documents (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  title TEXT NOT NULL, category kb_category NOT NULL,
  source_type kb_source NOT NULL, source TEXT,      -- nama file / URL
  storage_path TEXT,                                -- object storage (S3/Supabase)
  raw_content TEXT, checksum TEXT,                  -- deteksi duplikat / perubahan
  status TEXT DEFAULT 'pending' CHECK (status IN ('pending','processing','indexed','failed','empty')),
  is_active BOOLEAN DEFAULT true, chunk_count INT DEFAULT 0,
  effective_date DATE, regulation_number TEXT,      -- mis. "Permen PUPR 8/2022"
  uploaded_by UUID REFERENCES users(id),
  created_at TIMESTAMPTZ DEFAULT now(), indexed_at TIMESTAMPTZ
);
CREATE TABLE kb_chunks (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  document_id UUID NOT NULL REFERENCES kb_documents(id) ON DELETE CASCADE,
  chunk_index INT NOT NULL,
  content TEXT NOT NULL,
  token_count INT,
  metadata JSONB DEFAULT '{}',                       -- {category, page, heading}
  embedding VECTOR(1536),                            -- text-embedding-3-small
  tsv TSVECTOR GENERATED ALWAYS AS (to_tsvector('simple', content)) STORED,
  UNIQUE (document_id, chunk_index)
);
CREATE INDEX kb_chunks_embedding_hnsw ON kb_chunks USING hnsw (embedding vector_cosine_ops);
CREATE INDEX kb_chunks_tsv_gin ON kb_chunks USING gin (tsv);    -- hybrid search (BM25-like)

-- ---------- CHAT ----------
CREATE TABLE chat_sessions (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  visitor_id TEXT, visitor_name TEXT, ip_hash TEXT, user_agent TEXT,
  services TEXT[] DEFAULT '{}', intents TEXT[] DEFAULT '{}',
  message_count INT DEFAULT 0, off_topic_count INT DEFAULT 0,
  escalated BOOLEAN DEFAULT false, escalated_at TIMESTAMPTZ,
  wa_admin_id UUID REFERENCES wa_admin_numbers(id),
  wa_clicked BOOLEAN DEFAULT false, wa_clicked_at TIMESTAMPTZ,
  started_at TIMESTAMPTZ DEFAULT now(), last_message_at TIMESTAMPTZ
);
CREATE TABLE chat_messages (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  session_id UUID NOT NULL REFERENCES chat_sessions(id) ON DELETE CASCADE,
  role msg_role NOT NULL, content TEXT NOT NULL,
  intent JSONB,                                      -- {intents, score, services}
  retrieved_chunk_ids UUID[],                        -- audit jejak RAG
  off_topic BOOLEAN DEFAULT false,
  latency_ms INT, tokens_in INT, tokens_out INT,
  created_at TIMESTAMPTZ DEFAULT now()
);
CREATE INDEX chat_messages_session_idx ON chat_messages (session_id, created_at);

-- ---------- LEADS ----------
CREATE TABLE leads (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  source lead_source NOT NULL, status lead_status NOT NULL DEFAULT 'baru',
  name TEXT NOT NULL, company TEXT, email TEXT, phone TEXT,
  service TEXT, message TEXT, intent_labels TEXT[],
  session_id UUID UNIQUE REFERENCES chat_sessions(id),
  assigned_to UUID REFERENCES users(id),
  notes TEXT, estimated_value NUMERIC(14,2),
  created_at TIMESTAMPTZ DEFAULT now(), updated_at TIMESTAMPTZ DEFAULT now()
);
CREATE INDEX leads_status_idx ON leads (status, created_at DESC);
CREATE TABLE lead_activities (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  lead_id UUID REFERENCES leads(id) ON DELETE CASCADE,
  actor_id UUID REFERENCES users(id), action TEXT, payload JSONB,
  created_at TIMESTAMPTZ DEFAULT now()
);

-- ---------- AUDIT ----------
CREATE TABLE audit_logs (
  id BIGSERIAL PRIMARY KEY, actor_id UUID, entity TEXT, entity_id TEXT,
  action TEXT, diff JSONB, ip INET, created_at TIMESTAMPTZ DEFAULT now()
);

-- ---------- RETRIEVAL (Hybrid: vector + keyword) ----------
CREATE OR REPLACE FUNCTION match_kb_chunks(q_embedding VECTOR(1536), q_text TEXT, k INT DEFAULT 5)
RETURNS TABLE (id UUID, document_id UUID, content TEXT, score FLOAT) LANGUAGE sql STABLE AS $$
  SELECT c.id, c.document_id, c.content,
         0.75 * (1 - (c.embedding <=> q_embedding)) +
         0.25 * ts_rank(c.tsv, plainto_tsquery('simple', q_text)) AS score
  FROM kb_chunks c JOIN kb_documents d ON d.id = c.document_id
  WHERE d.is_active
  ORDER BY score DESC LIMIT k;
$$;`;

export const MONGO_MAP = [
  ["users", "Admin CMS (bcrypt, JWT httpOnly cookie)"],
  ["settings", "profile, wa_numbers[], ai{threshold, top_k, extra_instructions}"],
  ["services · portfolio · articles · faqs", "Konten CMS website"],
  ["kb_documents", "Metadata dokumen + raw content + status indeks"],
  ["kb_chunks", "Chunk teks + text index berbobot (title×2) untuk retrieval"],
  ["chat_sessions", "Agregat sesi: layanan, intent, eskalasi, klik WA"],
  ["chat_messages", "Riwayat percakapan + intent + sumber RAG + flag off-topic"],
  ["leads", "Lead website & chatbot, status pipeline, catatan"],
  ["login_attempts", "Proteksi brute-force (5x gagal = kunci 15 menit)"],
];

export const ESCALATION_PY = `# backend/ai_brain.py — Intent Detection berbobot
INTENT_RULES = {
  "harga":     {"label": "Menanyakan harga/biaya",        "weight": 3,
                "patterns": [r"\\bharga", r"\\bbiaya", r"\\btarif", r"\\bbudget", r"\\bnego"]},
  "penawaran": {"label": "Meminta penawaran resmi / MoU", "weight": 3,
                "patterns": [r"penawaran", r"\\bmou\\b", r"\\bkontrak", r"quotation", r"\\bspk\\b"]},
  "urgent":    {"label": "Ingin diproses segera",         "weight": 3,
                "patterns": [r"hari ini", r"\\bsegera", r"secepatnya", r"(mau|ingin) (di)?proses"]},
  "berkas":    {"label": "Mengirim / menyiapkan berkas",  "weight": 3,
                "patterns": [r"kirim (berkas|dokumen|file)", r"\\bupload", r"dokumen (saya|kami) (siap|lengkap)"]},
  "kontak":    {"label": "Meminta kontak admin",          "weight": 2,
                "patterns": [r"whats ?app", r"\\bwa\\b", r"nomor (admin|cs)", r"\\bhubungi"]},
}

def detect_intent(text: str, threshold: int = 3) -> dict:
    t = text.lower()
    hits = [k for k, r in INTENT_RULES.items() if any(re.search(p, t) for p in r["patterns"])]
    score = sum(INTENT_RULES[k]["weight"] for k in hits)
    services = [s for s, ps in SERVICE_RULES.items() if any(re.search(p, t) for p in ps)]
    return {"intents": hits, "labels": [INTENT_RULES[k]["label"] for k in hits],
            "score": score, "escalate": score >= threshold, "services": services}

def build_wa_message(session: dict, last_user_msg: str) -> str:
    needs = "\\n".join(f"  - {m[:160]}" for m in session.get("user_snippets", [])[-3:])
    return (
        "Halo Admin PT Rikonstruksi Utama,\\n"
        "Saya ingin konsultasi lanjutan dari Virtual Assistant website.\\n\\n"
        "*RINGKASAN KEBUTUHAN*\\n"
        f"- Nama: {session.get('visitor_name') or '(belum disebutkan)'}\\n"
        f"- Layanan diminati: {', '.join(session.get('services') or []) or 'Belum spesifik'}\\n"
        f"- Sinyal kebutuhan: {', '.join(session.get('intent_labels') or [])}\\n"
        f"- Pertanyaan saya:\\n{needs}\\n"
        f"- Ref. Sesi: #{session['id'][:8].upper()}\\n\\n"
        "Mohon informasi penawaran resmi & langkah selanjutnya. Terima kasih."
    )

def wa_link(number: str, message: str) -> str:
    digits = re.sub(r"\\D", "", number)
    if digits.startswith("0"): digits = "62" + digits[1:]
    return f"https://wa.me/{digits}?text={urllib.parse.quote(message)}"

# backend/server.py — di akhir stream SSE
off_topic = REFUSAL_TEXT[:60] in full_answer
if intent["escalate"] and not off_topic:
    number = await pick_wa_number(session_id)          # round-robin nomor aktif
    msg = build_wa_message(session, user_message)
    escalation = {"trigger": intent["labels"], "message": msg,
                  "admin_name": number["name"], "wa_link": wa_link(number["number"], msg)}
    await upsert_escalation_lead(session, msg)          # otomatis masuk Lead Tracker
yield sse({"type": "done", "escalation": escalation})`;

export const ESCALATION_JSX = `// frontend ChatWidget.jsx — render tombol handoff
if (ev.type === "done") patchLast((m) => ({ ...m, escalation: ev.escalation }));

{m.escalation?.wa_link && (
  <a href={m.escalation.wa_link} target="_blank" rel="noreferrer"
     onClick={() => api.post(\`/chat/\${sid}/handoff-click\`)}      // tracking konversi
     data-testid="chatbot-wa-escalation-button">
    Konsultasi Lanjutan via WhatsApp Admin
  </a>
)}`;

export const WIREFRAME = `┌──────────────┬──────────────────────────────────────────────────────────────────────┐
│ ▣ RIKONSTRUKSI│  AI BRAIN · RAG                                   [ + Tambah Dokumen ] │
│   UTAMA       │  AI Knowledge Base Manager                                            │
│──────────────│  Dokumen referensi yang dipakai Virtual Assistant…                    │
│ ▢ Overview    │ ┌───────────┬───────────────┬──────────────┬────────────┐              │
│ ▢ Konten      │ │ DOKUMEN 4 │ CHUNK 18      │ AKTIF AI 4   │ KATEGORI 4 │              │
│ ▢ Lead        │ └───────────┴───────────────┴──────────────┴────────────┘              │
│ ▣ Knowledge   │ [Semua(4)] [Regulasi(1)] [Persyaratan(1)] [Matrix(1)] [Profil(1)]      │
│   Base        │ ┌──────────────────────────────────────────────────┐ ┌───────────────┐ │
│ ▢ Chatbot     │ │ DOKUMEN          KATEGORI  STATUS  CHUNK AKTIF AKSI│ │ UJI RETRIEVAL │ │
│   Analytics   │ │ 📄 Ringkasan UU…  Regulasi  INDEXED  5   (●)  👁 ⟳ 🗑│ │ [query…][🔍]  │ │
│ ▢ Profil      │ │ 📄 Persyaratan…   Persyar.  INDEXED  6   (●)  👁 ⟳ 🗑│ │ #1 skor 3.2   │ │
│               │ │ 🔗 jdih.pu.go.id  Regulasi  INDEXED  9   (○)  👁 ⟳ 🗑│ │  Persyaratan… │ │
│               │ │ 📄 Matrix Estim…  Matrix    INDEXED  4   (●)  👁 ⟳ 🗑│ │ #2 skor 2.7   │ │
│──────────────│ └──────────────────────────────────────────────────┘ └───────────────┘ │
│ admin@…       │                                                                       │
│ ⎋ Keluar      │ ┌ MODAL: Tambah Dokumen ─────────────┐ ┌ MODAL: Editor Dokumen ───────┐ │
└──────────────┘ │ Kategori [▾]  Judul [_______]       │ │ Judul / Kategori   │ CHUNK #0 │ │
                  │ [PDF/TXT] [URL] [Teks Manual]       │ │ ┌───────────────┐  │ CHUNK #1 │ │
                  │ ┌ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ┐    │ │ │ raw content   │  │ CHUNK #2 │ │
                  │   ⬆ Klik untuk memilih PDF          │ │ └───────────────┘  │   …      │ │
                  │ └ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ┘    │ │ [Simpan & Re-index]           │ │
                  │ [ Unggah & Indeks ]                 │ └───────────────────────────────┘ │
                  └─────────────────────────────────────┘                                   `;

export const STACK = [
  ["Frontend Website", "Next.js 15 (App Router, SSG/ISR untuk SEO) + Tailwind CSS + shadcn/ui", "React 19 + Tailwind + shadcn/ui (SPA)"],
  ["CMS & Backend", "FastAPI (Python) — API, ingestion worker (Celery/RQ), auth RBAC", "FastAPI (Python) monolith modular"],
  ["AI Framework", "LangChain / LlamaIndex + OpenAI GPT-4o / GPT-5.x + text-embedding-3-small", "OpenAI GPT-5.4 via Emergent LLM Key + RAG custom"],
  ["Database", "PostgreSQL 16 + pgvector (HNSW) — hybrid search vector + tsvector", "MongoDB + text index berbobot"],
  ["File Storage", "S3 / Supabase Storage (PDF asli, versi dokumen)", "Teks diekstrak & disimpan di DB"],
  ["Integrasi WA", "WhatsApp Business Cloud API (template + webhook) atau wa.me deep link", "wa.me deep link + ringkasan terformat"],
  ["Infra", "Docker + Kubernetes / Vercel (FE) + Cloud Run (BE), Redis cache & rate limit", "Kubernetes (preview)"],
];

export const SECURITY = [
  "JWT access token di httpOnly + Secure + SameSite cookie; refresh token 7 hari.",
  "Password bcrypt; proteksi brute-force (5 percobaan → kunci 15 menit).",
  "Seluruh endpoint /api/admin/* dilindungi dependency auth; RBAC (superadmin/admin/editor/sales) di produksi.",
  "Validasi input Pydantic (batas panjang pesan 2.000 karakter, ukuran file 15MB, whitelist kategori & tipe file).",
  "Prompt-injection guard: konteks KB dibungkus tag, instruksi menolak permintaan ubah peran / bocorkan system prompt.",
  "Rate limit chat per IP/sesi (Redis, produksi) + CAPTCHA pada form lead untuk anti-spam.",
  "Audit log perubahan konten & KB; enkripsi at-rest DB; PII (no. HP) di-mask di analytics.",
  "Secrets via environment variables (tidak di-hardcode), CORS dibatasi ke domain resmi.",
];

export const NEXT_STRUCTURE = `rikonstruksi/
├── apps/
│   ├── web/                      # Next.js 15 (publik, SEO)
│   │   ├── app/(site)/page.tsx           # landing (ISR)
│   │   ├── app/(site)/layanan/[slug]/    # halaman layanan
│   │   ├── app/(site)/artikel/[slug]/    # blog SSG + JSON-LD
│   │   ├── app/admin/(cms)/...           # dashboard CMS (client)
│   │   └── components/chat/ChatWidget.tsx
│   └── api/                      # FastAPI
│       ├── app/main.py
│       ├── app/routers/{auth,content,leads,kb,chat,analytics}.py
│       ├── app/ai/{prompt.py,intent.py,retriever.py,handoff.py}
│       ├── app/ingest/{pdf.py,url.py,chunker.py,embedder.py}
│       ├── app/db/{models.py,session.py}  # SQLAlchemy + pgvector
│       └── worker.py                      # Celery: indexing async
└── infra/{docker-compose.yml, k8s/}`;
